import { Controller } from "@hotwired/stimulus"

// Tracks the heading nearest the reading position without continuous scroll work.
// Called by Stimulus when the SOP reader enters the page.
export default class extends Controller {
  static targets = ["sectionLink"]

  // Starts observing rendered section headings and highlights the initial section.
  // Called by Stimulus when the SOP reader connects after normal or Turbo navigation.
  connect() {
    this.headings = this.sectionLinkTargets
      .map((link) => document.getElementById(link.getAttribute("href").slice(1)))
      .filter((heading) => heading && this.element.contains(heading))
    this.visibleHeadings = new Set()

    if (this.headings.length === 0) return

    this.markCurrent(decodeURIComponent(window.location.hash.slice(1)) || this.headings[0].id)

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) this.visibleHeadings.add(entry.target)
        else this.visibleHeadings.delete(entry.target)
      })

      // The last heading in the top reading band is the current section.
      const currentHeading = this.headings.findLast((heading) => this.visibleHeadings.has(heading))
      if (currentHeading) this.markCurrent(currentHeading.id)
    }, { rootMargin: "-88px 0px -65% 0px" })

    this.headings.forEach((heading) => this.observer.observe(heading))
  }

  // Releases heading observation before Turbo replaces the reader.
  // Called by Stimulus when the SOP reader disconnects.
  disconnect() {
    this.observer?.disconnect()
  }

  // Highlights the destination as soon as a table-of-contents link is selected.
  // Called by an On this page link on click->sop-source-navigation#selectSection.
  selectSection(event) {
    this.markCurrent(event.currentTarget.getAttribute("href").slice(1))
  }

  // Updates the active link and its accessible current-location state.
  // Called by the heading observer and table-of-contents click action.
  markCurrent(id) {
    this.sectionLinkTargets.forEach((link) => {
      const isCurrent = link.getAttribute("href") === `#${id}`
      link.classList.toggle("is-current", isCurrent)
      if (isCurrent) link.setAttribute("aria-current", "location")
      else link.removeAttribute("aria-current")
    })
  }
}
